import React, { ChangeEvent, FC, useCallback } from "react";
import { ResizeTransitionContainer } from "@cart-ui/widgets/ResizeTransitionContainer";
import { TextButton } from "@cart-ui/components/text-button";
import {
    InstructionsContentStyled,
    InstructionsTitleStyled,
    NoteStyled,
    TextareaContainerStyled,
    TextareaContentStyled,
    TextareaStyled,
    StyledResizeWrapper,
} from "./CartRestaurantSuggestion.style";
import { RESTAURANT_SPECIAL_INSTRUCTIONS } from "@cart-ui/constants/TextDict";

import { isEmpty, useEffectOnce } from "@swiggy-private/core-ui";
import { COMMENT_BOX_TEST_ID } from "@swiggy-private/cart-test-ids";
import { DEFAULT_PLACEHOLDER, SAVE } from "./Constants";

export interface CartRestaurantSuggestionTextProps {
    placeholder?: string;
    specialInstruction: string;
    isOptedInRx: boolean;
    handleRestaurantSuggestionChange: (note: string) => void;
    note: string;
    textAreaActive: boolean;
    setNote: React.Dispatch<React.SetStateAction<string>>;
    setTextAreaActive: React.Dispatch<React.SetStateAction<boolean>>;
    onCookingInstructionsFocus?: () => void;
    onCookingInstructionsBlur?: () => void;
    onCookingInstructionsClick?: (element: HTMLElement) => void;
}
// above regex syntax break in es5 due to no support for unicode params in regex
const EMOTICONS_REGEX =
    /(?:[\xA9\xAE\u2122\u23E9-\u23EF\u23F3\u23F8-\u23FA\u24C2\u25B6\u2600-\u27BF\u2934\u2935\u2B05-\u2B07\u2B1B\u2B1C\u2B50\u2B55\u3030\u303D\u3297\u3299]|\uD83C[\uDC04\uDCCF\uDD70\uDD71\uDD7E\uDD7F\uDD8E\uDD91-\uDE51\uDF00-\uDFFF]|\uD83D[\uDC00-\uDE4F\uDE80-\uDEFF]|\uD83E[\uDD00-\uDDFF])/g;

const CartRestaurantSuggestionText: FC<CartRestaurantSuggestionTextProps> = (props) => {
    const {
        placeholder = DEFAULT_PLACEHOLDER,
        isOptedInRx,
        handleRestaurantSuggestionChange,
        note,
        textAreaActive,
        setNote,
        setTextAreaActive,
        onCookingInstructionsFocus,
        onCookingInstructionsBlur,
        onCookingInstructionsClick,
    } = props;
    const suggestionBoxRef = React.useRef<HTMLTextAreaElement>(null);

    const onNoteClick = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
            onCookingInstructionsClick?.(e.currentTarget);
            setTextAreaActive(true);
        },
        [setTextAreaActive, onCookingInstructionsClick],
    );

    const onSave = useCallback(() => {
        setTextAreaActive(false);

        if (note && suggestionBoxRef?.current?.value) {
            let boxNote = suggestionBoxRef.current.value;
            boxNote = boxNote.replace(/^\s+|\s+$/g, "");

            setNote(boxNote);
            handleRestaurantSuggestionChange?.(boxNote);
        }
    }, [setTextAreaActive, note, setNote, handleRestaurantSuggestionChange]);

    const onTextAreaBlur = useCallback(() => {
        onCookingInstructionsBlur?.();
        if (!note) {
            handleRestaurantSuggestionChange?.("");
            setTextAreaActive(false);
        }
    }, [handleRestaurantSuggestionChange, note, setTextAreaActive, onCookingInstructionsBlur]);

    const onInputChangeCallback = (event: ChangeEvent<HTMLTextAreaElement>): void => {
        if (event.target) {
            let note = event.target.value;

            note = note.replace(EMOTICONS_REGEX, "");

            // update the formatted text
            setNote(note);
        }
    };

    const rxSpecialInstruction = isOptedInRx
        ? RESTAURANT_SPECIAL_INSTRUCTIONS.OPT_IN_CONTENT
        : RESTAURANT_SPECIAL_INSTRUCTIONS.OPT_OUT_CONTENT;

    useEffectOnce(() => {
        return (): void => {
            setTextAreaActive(false);
        };
    });

    return (
        <StyledResizeWrapper textAreaActive={textAreaActive}>
            <ResizeTransitionContainer>
                {textAreaActive && (
                    <TextareaContainerStyled>
                        <TextareaContentStyled>
                            <TextareaStyled
                                autoFocus
                                ref={suggestionBoxRef}
                                rows={1}
                                placeholder={placeholder}
                                value={note}
                                onChange={onInputChangeCallback}
                                data-testid={COMMENT_BOX_TEST_ID}
                                onFocus={onCookingInstructionsFocus}
                                onBlur={onTextAreaBlur}
                                maxLength={1000}
                            />
                            {!isEmpty(note) && <TextButton text={SAVE} onClick={onSave} />}
                        </TextareaContentStyled>

                        <InstructionsTitleStyled>{RESTAURANT_SPECIAL_INSTRUCTIONS.TITLE}</InstructionsTitleStyled>
                        <InstructionsContentStyled>{rxSpecialInstruction}</InstructionsContentStyled>
                    </TextareaContainerStyled>
                )}

                {!textAreaActive && note && <NoteStyled onClick={onNoteClick}>{note}</NoteStyled>}
            </ResizeTransitionContainer>
        </StyledResizeWrapper>
    );
};

export default CartRestaurantSuggestionText;
